import { FC } from 'react';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import * as R from '../../clients/mira/types/Resource';
import { selectDomainForCurrentUser } from '../../selectors/v2/domains';
import {
  selectIsEnterpriseUser,
  selectUserProfile,
} from '../../selectors/user';
import PaperModalV2 from 'raydiant-elements/core/PaperModalV2';
import Text from '@raydiant/raydial/components/Text';
import Select, { SelectItem } from '@raydiant/raydial/components/Select';
import PlaylistSelectorActionBarV2 from './PlaylistSelectorActionBarV2';
import PlaylistUserIcon from '../../../src/pages/LibraryPage/v2/assets/playlist-user-icon.svg';
import { hasRoleOrHigher } from '../../utilities';
import LibraryTreeV2Item from '../LibraryTree/v2/LibraryTreeV2Item';
import Button from '@raydiant/raydial/components/Button';
import { BACK_BUTTON_ICON } from '../../pages/LocationsPage/DeviceShare/DeviceShare';
import LibraryTreeV2 from '../LibraryTree/v2';
import { selectSearchQuery } from './selectors';
import { Content, Playlist } from '@raydiant/api-client-js';

export const DEFAULT_QUERY = {
  scope: 'owned',
  profile_id: '',
  folder_id: '',
  search: '',
  sort_by: '',
  sort_direction: '',
  back_to: '',
};

interface FolderState {
  [key: string]: boolean;
}

interface LibraryFolder {
  [key: string]: FolderState;
}

export interface PlaylistSelectorV2Props {
  batch?: boolean;
  selectedPlaylistId?: string | null;
  onSelectPlaylist?: (playlistId: string) => void;
  onSelectBatchPlaylist?: (playlist: Playlist) => void;
  onNewPlaylist?: () => void;
}

const PlaylistSelectorV2: FC<PlaylistSelectorV2Props> = ({
  batch,
  selectedPlaylistId,
  onSelectPlaylist,
  onSelectBatchPlaylist,
  onNewPlaylist,
}) => {
  // Selectors
  const [libraryFolder, setLibraryFolder] = useState<LibraryFolder>({
    owned: {},
    shared: {},
  });
  const [scope, setScope] = useState('owned');
  const [selectedProfileId, setSelectedProfileId] = useState<string>('');
  const [query, setQuery] = useState(DEFAULT_QUERY);
  const [updatedPlaylistId, setUpdatedPlaylistId] = useState<string>(
    selectedPlaylistId || '',
  );

  const isEnterpriseUser = useSelector(selectIsEnterpriseUser);
  const currentUser = useSelector(selectUserProfile);
  const domain = useSelector(selectDomainForCurrentUser);
  const searchQuery = useSelector(selectSearchQuery);

  // API hooks

  const domainProfiles = domain?.r.profiles || [];
  const filteredDomainProfiles = domainProfiles.filter(
    (profile) => profile.id !== currentUser?.id,
  );
  const sortedDomainProfiles = [...filteredDomainProfiles].sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  const toggleFolder = (folderId: string) => {
    let nextFolder: LibraryFolder;

    const groupKey = scope === 'users' ? selectedProfileId : scope;
    const selectedGroup = libraryFolder?.[groupKey] || {};
    const selectedFolderId = folderId;

    nextFolder = {
      ...libraryFolder,
      [groupKey]: {
        ...selectedGroup,
        [selectedFolderId]: !selectedGroup[selectedFolderId] || false,
      },
    };

    setLibraryFolder(nextFolder);
  };

  const handleQueryChange = (key: string) => {
    setSelectedProfileId('');
    if (key === 'owned') {
      setQuery({ ...query, scope: 'owned', profile_id: '' });
    }
    if (key === 'shared') {
      setQuery({ ...query, scope: 'shared', profile_id: '' });
    }
    setScope(key);
  };

  // Render

  let selectedProfile: R.ResourceProfile | undefined;

  if (isEnterpriseUser && domain) {
    selectedProfile = domain.r.profiles.find((p) => p.id === selectedProfileId);
  } else if (!isEnterpriseUser && currentUser) {
    // Map full user profile to a resource profile.
    selectedProfile = {
      id: currentUser.id,
      email: currentUser.email,
      name: currentUser.name,
      thumbnailUrl: currentUser.thumbnailUrl || '',
      domainId: currentUser.domainId,
      domainRole: currentUser.domainRole,
    };
  }

  const handlePlaylistClick = (id: string) => {
    if (id === updatedPlaylistId) {
      setUpdatedPlaylistId('');
    } else {
      setUpdatedPlaylistId(id);
    }
  };

  const handleUserClick = (id: string) => {
    setLibraryFolder((prevLibraryFolder) => ({
      ...prevLibraryFolder,
      [id]: prevLibraryFolder[id] || {},
    }));

    setSelectedProfileId(id);
  };

  const handleAssignPlaylist = (content: Content) => {
    if (batch) {
      onSelectBatchPlaylist &&
        onSelectBatchPlaylist({
          id: content.id,
          name: content.name,
          description: '',
          scheduleType: '',
          tzid: null,
          startDatetime: null,
          endDatetime: null,
          recurrenceRule: null,
          items: [],
          isRuleOnItems: false,
          rule: null,
          resource: {
            id: content.resourceId,
            updatedAt: content.updatedAt,
            createdAt: content.createdAt,
            deletedAt: null,
            lastActivityAt: content.lastActivityAt,
            parentFolderId: null,
            profile: {
              id: content.ownedBy.id,
              email: content.ownedBy.email,
              name: content.ownedBy.name,
              thumbnailUrl: content.ownedBy.thumbnailUrl,
              domainId: content.ownedBy.domainId,
              domainRole: content.ownedBy.domainRole,
            },
            domain: null,
            r: {
              resourceACLs: [],
              tags: [],
            },
          },
        });
    } else {
      onSelectPlaylist && onSelectPlaylist(content.id);
    }
  };

  if (!currentUser) return null;

  const hasAccessToUserLibraries = hasRoleOrHigher('standard', currentUser);

  const renderLibraryTree = () => {
    switch (true) {
      case searchQuery.length > 0:
        return (
          <div>
            <div className="flex items-center mt-4 mb-3">
              <div className="text-black ml-4 truncate">
                <Text variant="smallSubheader">Search Results</Text>
              </div>
            </div>

            <LibraryTreeV2
              openedFolders={libraryFolder[selectedProfileId] || {}}
              selectedPlaylistId={updatedPlaylistId}
              onSelectPlaylist={handlePlaylistClick}
              onAssignPlaylist={handleAssignPlaylist}
              onFolderClick={toggleFolder}
              scope="owned"
              profileId={selectedProfileId}
              folderId=""
              isRootFolder
            />
          </div>
        );
      case scope === 'users' && !selectedProfileId:
        return sortedDomainProfiles?.map((user) => (
          <LibraryTreeV2Item
            key={user.id}
            iconUrl={PlaylistUserIcon}
            label={user.name}
            onClick={() => handleUserClick(user.id)}
          />
        ));
      case scope === 'users':
        return (
          <div>
            <div className="flex items-center mt-4 mb-3">
              <Button
                variant="unstyled"
                onPress={() => setSelectedProfileId('')}
              >
                {BACK_BUTTON_ICON}
              </Button>
              {selectedProfile && (
                <div className="text-black ml-4 truncate">
                  <Text variant="smallSubheader">
                    {selectedProfile.name}'s Library
                  </Text>
                </div>
              )}
            </div>

            <LibraryTreeV2
              openedFolders={libraryFolder[selectedProfileId] || {}}
              selectedPlaylistId={updatedPlaylistId}
              onSelectPlaylist={handlePlaylistClick}
              onAssignPlaylist={handleAssignPlaylist}
              onFolderClick={toggleFolder}
              scope="owned"
              profileId={selectedProfileId}
              folderId=""
              isRootFolder
            />
          </div>
        );
      default:
        return (
          <LibraryTreeV2
            openedFolders={libraryFolder[scope] || {}}
            selectedPlaylistId={updatedPlaylistId}
            onSelectPlaylist={handlePlaylistClick}
            onAssignPlaylist={handleAssignPlaylist}
            onFolderClick={toggleFolder}
            scope={scope}
            profileId=""
            folderId=""
            isRootFolder
          />
        );
    }
  };

  const renderPlaylistSelectorV2 = () => {
    return (
      <PaperModalV2.Body className="flex flex-col mb-4 gap-4 h-full">
        <div>
          <Text variant="subheader" className="text-black mb-1 flex">
            Select Playlist
          </Text>
        </div>
        <div>
          <Select
            label="Library"
            className="w-full"
            isDisabled={!isEnterpriseUser}
            selectedKey={scope}
            onSelectionChange={(e) => handleQueryChange(e as string)}
          >
            <SelectItem id="owned">My Library</SelectItem>
            {isEnterpriseUser && (
              <>
                <SelectItem id="shared">Shared With Me</SelectItem>
                {hasAccessToUserLibraries && (
                  <SelectItem id="users">User Libraries</SelectItem>
                )}
              </>
            )}
          </Select>
        </div>
        <div>
          <PlaylistSelectorActionBarV2
            selectedProfile={selectedProfile}
            onNewPlaylist={onNewPlaylist}
            sortOnly={!(scope === 'owned')}
          />
        </div>
        <div className="flex flex-col flex-1 overflow-auto">
          {renderLibraryTree()}
        </div>
      </PaperModalV2.Body>
    );
  };

  return <>{renderPlaylistSelectorV2()}</>;
};

export default PlaylistSelectorV2;
